import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Send, Building, Factory, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { usePageContent } from '../hooks/usePageContent';

const API = process.env.REACT_APP_BACKEND_URL;

const CONTACT_DEFAULTS = {
  hero_title: 'Parlons de votre projet',
  hero_subtitle: "Notre équipe est à votre disposition pour répondre à toutes vos questions et vous accompagner dans vos projets.",
  form_title: 'Envoyez-nous un message',
  info_title: 'Informations de contact',
  map_title: 'Retrouvez-nous en France',
  map_subtitle: '4 sites à votre service sur le territoire national',
};

const PageHeader = ({ c }) => {
  return (
    <section className="pt-32 pb-20 bg-[#0e1c3f]" data-testid="contact-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Contact
          </p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6">
            {c.hero_title}
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            {c.hero_subtitle}
          </p>
        </div>
      </div>
    </section>
  );
};

const ContactForm = ({ c }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/api/contact`, formData);
      setSuccess(true);
      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-[#0e1c3f] p-12 lg:p-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-[#0078b7] rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#ff65c3] rounded-full blur-3xl" />
        </div>
        <div className="relative">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="h-10 w-10 text-emerald-400" />
          </div>
          <h3 className="font-heading font-bold text-3xl text-white mb-4">
            Message envoyé !
          </h3>
          <p className="text-slate-400 mb-10 max-w-md mx-auto">
            Merci pour votre message. Notre équipe vous contactera dans les plus brefs délais.
          </p>
          <Button 
            onClick={() => setSuccess(false)}
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-sm px-8"
          >
            Envoyer un autre message
          </Button>
        </div>
      </div>
    );
  }

  const inputBase = "bg-transparent border-0 border-b-2 rounded-none px-0 py-3 text-white placeholder:text-white/30 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors";

  return (
    <form onSubmit={handleSubmit} className="bg-[#0e1c3f] p-8 lg:p-12 relative overflow-hidden" data-testid="contact-form">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#0078b7] rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#ff65c3] rounded-full blur-3xl" />
      </div>

      <div className="relative">
        <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-3">Contact</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-white uppercase mb-2">
          {c.form_title}
        </h2>
        <p className="text-white/40 text-sm mb-10">Les champs marqués d'un * sont obligatoires</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 mb-8">
          <div>
            <Label htmlFor="name" className={`text-sm font-medium mb-2 block transition-colors ${focusedField === 'name' ? 'text-[#0078b7]' : 'text-white/50'}`}>
              Nom complet *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              onFocus={() => setFocusedField('name')}
              onBlur={() => setFocusedField(null)}
              className={`${inputBase} ${focusedField === 'name' ? 'border-[#0078b7]' : 'border-white/20'}`}
              placeholder="Jean Martin"
              data-testid="input-name"
            />
          </div>
          <div>
            <Label htmlFor="email" className={`text-sm font-medium mb-2 block transition-colors ${focusedField === 'email' ? 'text-[#0078b7]' : 'text-white/50'}`}>
              Email *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              className={`${inputBase} ${focusedField === 'email' ? 'border-[#0078b7]' : 'border-white/20'}`}
              placeholder="sophie.martin@exemple.fr"
              data-testid="input-email"
            />
          </div>
          <div>
            <Label htmlFor="phone" className={`text-sm font-medium mb-2 block transition-colors ${focusedField === 'phone' ? 'text-[#0078b7]' : 'text-white/50'}`}>
              Téléphone
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
              className={`${inputBase} ${focusedField === 'phone' ? 'border-[#0078b7]' : 'border-white/20'}`}
              placeholder="01 55 34 79 20"
              data-testid="input-phone"
            />
          </div>
          <div>
            <Label htmlFor="company" className={`text-sm font-medium mb-2 block transition-colors ${focusedField === 'company' ? 'text-[#0078b7]' : 'text-white/50'}`}>
              Société
            </Label>
            <Input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              onFocus={() => setFocusedField('company')}
              onBlur={() => setFocusedField(null)}
              className={`${inputBase} ${focusedField === 'company' ? 'border-[#0078b7]' : 'border-white/20'}`}
              placeholder="Nom de votre société"
              data-testid="input-company"
            />
          </div>
        </div>

        <div className="mb-8">
          <Label htmlFor="subject" className={`text-sm font-medium mb-2 block transition-colors ${focusedField === 'subject' ? 'text-[#0078b7]' : 'text-white/50'}`}>
            Sujet *
          </Label>
          <Input
            id="subject"
            name="subject"
            type="text"
            required
            value={formData.subject}
            onChange={handleChange}
            onFocus={() => setFocusedField('subject')}
            onBlur={() => setFocusedField(null)}
            className={`${inputBase} ${focusedField === 'subject' ? 'border-[#0078b7]' : 'border-white/20'}`}
            placeholder="Demande de devis, question technique..."
            data-testid="input-subject"
          />
        </div>

        <div className="mb-10">
          <Label htmlFor="message" className={`text-sm font-medium mb-2 block transition-colors ${focusedField === 'message' ? 'text-[#0078b7]' : 'text-white/50'}`}>
            Message *
          </Label>
          <Textarea
            id="message"
            name="message"
            required
            rows={5}
            value={formData.message}
            onChange={handleChange}
            onFocus={() => setFocusedField('message')}
            onBlur={() => setFocusedField(null)}
            className={`${inputBase} resize-none ${focusedField === 'message' ? 'border-[#0078b7]' : 'border-white/20'}`}
            placeholder="Décrivez votre projet ou votre demande..."
            data-testid="input-message"
          />
        </div>

        <Button 
          type="submit" 
          disabled={loading}
          className="w-full bg-[#0078b7] hover:bg-[#0078b7]/80 text-white font-medium py-6 rounded-sm group transition-all"
          data-testid="submit-contact-btn"
        >
          {loading ? (
            <span className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Envoi en cours...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-3">
              Envoyer le message
              <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
};

const ContactInfo = ({ c }) => {
  return (
    <div className="space-y-6">
      {/* Main Contact Card */}
      <div className="bg-white p-8 lg:p-10 border border-slate-200 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0078b7] via-[#ff65c3] to-[#0078b7]" />
        <h2 className="font-display font-bold text-xl text-[#0e1c3f] uppercase mb-8">
          {c.info_title}
        </h2>
        <div className="space-y-6">
          <a href="mailto:contact@soprel-groupe.fr" className="flex items-center gap-4 group">
            <div className="w-12 h-12 bg-[#0e1c3f] group-hover:bg-[#0078b7] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Email</p>
              <p className="text-[#0e1c3f] font-heading font-bold group-hover:text-[#0078b7] transition-colors">
                contact@soprel-groupe.fr
              </p>
            </div>
          </a>
          <a href="tel:+33155347920" className="flex items-center gap-4 group">
            <div className="w-12 h-12 bg-[#0e1c3f] group-hover:bg-[#0078b7] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
              <Phone className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Téléphone</p>
              <p className="text-[#0e1c3f] font-heading font-bold group-hover:text-[#0078b7] transition-colors">
                01 55 34 79 20
              </p>
            </div>
          </a>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0e1c3f] rounded-sm flex items-center justify-center flex-shrink-0">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-0.5">Siège social</p>
              <p className="text-[#0e1c3f] font-heading font-bold">
                32 rue de Cambrai
              </p>
              <p className="text-slate-500 text-sm">75019 Paris</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick info cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-[#0e1c3f] p-6 text-center">
          <p className="font-display font-bold text-3xl text-[#0078b7] mb-1">4</p>
          <p className="text-white/50 text-xs uppercase tracking-wider font-medium">Sites en France</p>
        </div>
        <div className="bg-[#0e1c3f] p-6 text-center">
          <p className="font-display font-bold text-3xl text-[#ff65c3] mb-1">1957</p>
          <p className="text-white/50 text-xs uppercase tracking-wider font-medium">Année de création</p>
        </div>
      </div>

      {/* Horaires */}
      <div className="bg-slate-50 border border-slate-200 p-8">
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-[#0e1c3f] mb-4">Horaires d'ouverture de bureau</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Lundi - Vendredi</span>
            <span className="text-[#0e1c3f] font-medium">8h00 - 17h30</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Samedi - Dimanche</span>
            <span className="text-slate-400">Fermé</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SOPREL_LOCATIONS = [
  { name: 'Siège Social - Paris', lat: 48.8936, lng: 2.3744, address: '32 rue de Cambrai, 75019 Paris' },
  { name: 'Production - St Pierre La Garenne', lat: 49.1667, lng: 1.3333, address: '58 rue du Fond du Val, 27600 St Pierre La Garenne' },
  { name: 'Production - Esternay', lat: 48.7333, lng: 3.5567, address: 'ZI rue des Noues, 51130 Esternay' },
  { name: 'Production - Marolles sur Seine', lat: 48.3900, lng: 3.0567, address: '92 route de Bray, 77130 Marolles sur Seine' },
];

const GoogleMapSection = ({ c }) => {
  const [activeMarker, setActiveMarker] = useState(0);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setMapLoaded(true); },
      { threshold: 0.1 }
    );
    if (mapRef.current) observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  const activeLoc = SOPREL_LOCATIONS[activeMarker];
  const mapSrc = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(activeLoc.address)}&zoom=14&maptype=roadmap&language=fr`;

  return (
    <section className="bg-[#0e1c3f] py-20" data-testid="google-map-section" ref={mapRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-3">Nos implantations</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
            {c.map_title}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Map */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-sm border border-white/10">
            {mapLoaded ? (
              <iframe
                key={activeMarker}
                title={`SOPREL - ${activeLoc.name}`}
                src={mapSrc}
                className="w-full h-[300px] sm:h-[400px] lg:h-[500px]"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map-iframe"
              />
            ) : (
              <div className="w-full h-[450px] lg:h-[500px] bg-white/5 flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-[#0078b7] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Locations list */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
            {SOPREL_LOCATIONS.map((loc, i) => {
              const isHQ = i === 0;
              const isActive = activeMarker === i;
              return (
                <button
                  key={i}
                  onClick={() => setActiveMarker(i)}
                  className={`block w-full text-left p-5 border rounded-sm transition-all ${
                    isActive
                      ? 'bg-white/10 border-[#0078b7]'
                      : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                  }`}
                  data-testid={`map-location-${i}`}
                >
                  {isActive && <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#0078b7] to-[#ff65c3] rounded-t-sm" />}
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 transition-colors ${
                      isActive ? (isHQ ? 'bg-[#0078b7]' : 'bg-[#ff65c3]') : 'bg-white/10'
                    }`}>
                      {isHQ
                        ? <Building className={`h-5 w-5 ${isActive ? 'text-white' : 'text-white/50'}`} />
                        : <Factory className={`h-5 w-5 ${isActive ? 'text-white' : 'text-white/50'}`} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isActive ? (isHQ ? 'text-[#0078b7]' : 'text-[#ff65c3]') : 'text-white/40'}`}>
                        {isHQ ? 'Siège social' : 'Production'}
                      </p>
                      <p className={`font-heading font-bold text-sm mb-1 truncate transition-colors ${isActive ? 'text-white' : 'text-white/60'}`}>{loc.name.split(' - ')[1]}</p>
                      <p className="text-white/40 text-xs leading-relaxed">{loc.address}</p>
                    </div>
                    <MapPin className={`h-4 w-4 flex-shrink-0 mt-1 transition-colors ${isActive ? 'text-[#0078b7]' : 'text-white/20'}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactPage = () => {
  const { content: c } = usePageContent('contact', CONTACT_DEFAULTS);
  return (
    <div data-testid="contact-page">
      <PageHeader c={c} />
      <section className="py-24 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <ContactForm c={c} />
            </div>
            <div className="lg:col-span-2">
              <ContactInfo c={c} />
            </div>
          </div>
        </div>
      </section>
      <GoogleMapSection c={c} />
    </div>
  );
};

export default ContactPage;
